import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Layers, Gift, RotateCcw, CalendarClock, Pencil, X } from 'lucide-react';
import { salesApi, formatMoneyShort, serverError, CURRENCIES } from '../../lib/salesApi';

const CY = '#90E0EF';
const inputCls = 'bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-sm font-mono px-3 py-2.5 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]';
const CARD_TONE = { available: '#7ed4a0', reserved: '#e4c07a', sold: '#90E0EF', gifted: '#c58bf0' };

export default function SalesCardsPage() {
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // single add
  const [num, setNum] = useState('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [seller, setSeller] = useState('relative_gallery');
  const [adding, setAdding] = useState(false);

  // bulk
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  // draw date
  const [drawDate, setDrawDate] = useState('');
  const [drawDisplay, setDrawDisplay] = useState('');
  const [drawSaving, setDrawSaving] = useState(false);

  // gift recipient modal
  const [giftModal, setGiftModal] = useState(null); // { card, mode: 'gift' | 'edit' }
  const [gName, setGName] = useState('');
  const [gEmail, setGEmail] = useState('');
  const [gBusy, setGBusy] = useState(false);

  useEffect(() => {
    salesApi.drawSettings()
      .then((d) => { setDrawDate(d.draw_date || ''); setDrawDisplay(d.draw_date_display || ''); })
      .catch(() => {});
  }, []);

  async function saveDraw() {
    if (!drawDate) return;
    setDrawSaving(true);
    try {
      const d = await salesApi.saveDraw(drawDate);
      setDrawDate(d.draw_date || ''); setDrawDisplay(d.draw_date_display || '');
      toast.success(`Draw date set to ${d.draw_date_display}`);
    } catch (e) { toast.error(serverError(e)); } finally { setDrawSaving(false); }
  }

  function load() {
    setLoading(true);
    salesApi.cards(statusFilter === 'all' ? undefined : statusFilter)
      .then((d) => { setCards(d.cards || []); setStats(d.stats || {}); setTablesMissing(false); })
      .catch((e) => { if (e?.response?.status === 503) setTablesMissing(true); else toast.error(serverError(e)); })
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [statusFilter]);

  async function addSingle() {
    if (!num) return;
    setAdding(true);
    try {
      await salesApi.createCard({
        card_number: parseInt(num, 10),
        title: title || null,
        amount_minor: price ? Math.round(parseFloat(price) * 100) : null,
        currency, seller_scope: seller,
      });
      toast.success('Card added');
      setNum(''); setTitle(''); setPrice('');
      load();
    } catch (e) { toast.error(serverError(e)); } finally { setAdding(false); }
  }

  async function addBulk() {
    if (!bulkStart || !bulkEnd) return;
    setBulkBusy(true);
    try {
      const r = await salesApi.bulkCards({
        start: parseInt(bulkStart, 10), end: parseInt(bulkEnd, 10),
        amount_minor: bulkPrice ? Math.round(parseFloat(bulkPrice) * 100) : null, currency, seller_scope: seller,
      });
      toast.success(`Added ${r.created} cards (${r.skipped_existing} already existed)`);
      setBulkStart(''); setBulkEnd('');
      load();
    } catch (e) { toast.error(serverError(e)); } finally { setBulkBusy(false); }
  }

  async function del(id) {
    try { await salesApi.deleteCard(id); toast.success('Card removed'); load(); }
    catch (e) { toast.error(serverError(e)); }
  }

  function openGift(card) { setGiftModal({ card, mode: 'gift' }); setGName(''); setGEmail(''); }
  function openEditRecipient(card) { setGiftModal({ card, mode: 'edit' }); setGName(card.gifted_to_name || ''); setGEmail(card.gifted_to_email || ''); }

  async function submitGift() {
    if (!giftModal) return;
    setGBusy(true);
    const body = { name: gName.trim() || null, email: gEmail.trim() || null };
    try {
      if (giftModal.mode === 'edit') {
        await salesApi.giftRecipient(giftModal.card.id, body);
        toast.success('Recipient updated');
      } else {
        await salesApi.giftCard(giftModal.card.id, body);
        toast.success('Card withheld — counts as claimed, not a sale');
      }
      setGiftModal(null); load();
    } catch (e) { toast.error(serverError(e)); } finally { setGBusy(false); }
  }

  async function release(id) {
    try { await salesApi.releaseCard(id); toast.success('Card released back to available'); load(); }
    catch (e) { toast.error(serverError(e)); }
  }

  if (tablesMissing) {
    return <div className="border border-[#2a2416] bg-[#141207] px-6 py-8 text-center">
      <p className="font-outfit text-lg text-[#e4c07a]">Sales tables not initialised</p>
      <p className="text-xs font-mono text-[#aa9] mt-2">Run the SQL migration, then reload.</p>
    </div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* left — inventory */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          {['all', 'available', 'sold', 'gifted', 'reserved'].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`card-filter-${s}`}
              style={{ cursor: 'pointer', borderColor: statusFilter === s ? CY : '#222' }}
              className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.12em] border transition-colors">
              <span style={{ color: statusFilter === s ? CY : '#888' }}>{s} · {stats[s] ?? 0}</span>
            </button>
          ))}
        </div>
        <p className="text-[10px] font-mono text-[#5a5a5a] mb-4 leading-relaxed">
          <span style={{ color: CARD_TONE.gifted }}>Withheld / gifted</span> cards are ones you gave away or kept — they count toward the public claimed counter but are never a sale. Add a name &amp; email so they can take part in a <span style={{ color: CARD_TONE.gifted }}>draw</span>.
        </p>

        <div className="border border-[#1a1a1a]" data-testid="cards-table">
          <div className="grid grid-cols-[70px_1fr_84px_92px_64px] gap-2 px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0a0a0a] text-[9px] font-mono uppercase tracking-[0.14em] text-[#666]">
            <span>Card</span><span>Title</span><span className="text-right">Price</span><span>Status</span><span className="text-right">Actions</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
          ) : cards.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs font-mono text-[#555]">No cards in inventory.</p>
          ) : cards.map((c) => {
            const disp = c.display_status || c.status;
            return (
            <div key={c.id} data-testid={`card-row-${c.card_number_display}`} className="grid grid-cols-[70px_1fr_84px_92px_64px] gap-2 px-4 py-2.5 border-b border-[#141414] items-center">
              <span className="font-mono text-sm" style={{ color: CY }}>#{c.card_number_display}</span>
              <div className="min-w-0">
                <span className="block text-xs text-[#ccc] truncate">{c.title || '—'}</span>
                {disp === 'gifted' && (c.gifted_to_name || c.gifted_to_email) && (
                  <span className="block text-[10px] font-mono truncate" style={{ color: CARD_TONE.gifted }} data-testid={`card-recipient-label-${c.card_number_display}`}>
                    {c.gifted_to_name || ''}{c.gifted_to_name && c.gifted_to_email ? ' · ' : ''}{c.gifted_to_email || ''}
                  </span>
                )}
                {disp === 'gifted' && !c.gifted_to_name && !c.gifted_to_email && (
                  <span className="block text-[10px] font-mono truncate text-[#555]">no recipient — add for draw</span>
                )}
              </div>
              <span className="font-mono text-xs text-white text-right">{c.amount_minor != null ? formatMoneyShort(c.amount_minor, c.currency) : '—'}</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.1em]" style={{ color: CARD_TONE[disp] || '#888' }}>{disp}</span>
              <div className="flex items-center gap-2.5 justify-self-end">
                {disp === 'available' && (
                  <>
                    <button onClick={() => openGift(c)} data-testid={`card-gift-${c.card_number_display}`} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-[#c58bf0]" title="Withhold — give away or keep (counts as claimed, not a sale)"><Gift className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(c.id)} data-testid={`card-delete-${c.card_number_display}`} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-[#e88ba0]" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
                {disp === 'gifted' && (
                  <>
                    <button onClick={() => openEditRecipient(c)} data-testid={`card-recipient-${c.card_number_display}`} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-[#c58bf0]" title="Edit recipient (name / email for draw)"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => release(c.id)} data-testid={`card-release-${c.card_number_display}`} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-[#7ed4a0]" title="Release back to available"><RotateCcw className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(c.id)} data-testid={`card-delete-${c.card_number_display}`} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-[#e88ba0]" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* right — add */}
      <div className="space-y-6">
        <div className="border border-[#1a1a1a] bg-[#080808] p-5" data-testid="draw-date-panel">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-1 flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" /> Draw date</p>
          <p className="text-[10px] font-mono text-[#5a5a5a] mb-4 leading-relaxed">Shown on the This Is Humanity card page and in purchase emails.</p>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input type="date" className={inputCls} value={drawDate} onChange={(e) => setDrawDate(e.target.value)} data-testid="draw-date-input" />
            <button onClick={saveDraw} disabled={!drawDate || drawSaving} style={{ cursor: drawDate && !drawSaving ? 'pointer' : 'not-allowed', background: CY, color: '#050505' }}
              data-testid="draw-date-save" className="inline-flex items-center justify-center gap-2 px-4 text-[11px] font-mono uppercase tracking-[0.16em] disabled:opacity-40">
              {drawSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </button>
          </div>
          {drawDisplay && <p className="mt-3 text-[11px] font-mono" style={{ color: CY }} data-testid="draw-date-current">Draw — {drawDisplay}</p>}
        </div>

        <div className="border border-[#1a1a1a] bg-[#080808] p-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-4">Add a card</p>
          <div className="space-y-3">
            <input className={inputCls + ' w-full'} placeholder="Card number (e.g. 64)" value={num} inputMode="numeric" onChange={(e) => setNum(e.target.value.replace(/[^0-9]/g, ''))} data-testid="card-add-number" />
            <input className={inputCls + ' w-full'} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="card-add-title" />
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <input className={inputCls} placeholder="Price 50.00" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))} data-testid="card-add-price" />
              <select className={inputCls} value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ cursor: 'pointer' }}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <select className={inputCls + ' w-full'} value={seller} onChange={(e) => setSeller(e.target.value)} style={{ cursor: 'pointer' }}>
              <option value="relative_gallery">The Relative Gallery</option>
              <option value="mr_relative">Mr. Relative</option>
            </select>
            <button onClick={addSingle} disabled={!num || adding} style={{ cursor: num && !adding ? 'pointer' : 'not-allowed', background: CY, color: '#050505' }}
              data-testid="card-add-submit" className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] disabled:opacity-40">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Card
            </button>
          </div>
        </div>

        <div className="border border-[#1a1a1a] bg-[#080808] p-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#666] mb-4 flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Bulk add range</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <input className={inputCls} placeholder="From" inputMode="numeric" value={bulkStart} onChange={(e) => setBulkStart(e.target.value.replace(/[^0-9]/g, ''))} data-testid="bulk-start" />
            <input className={inputCls} placeholder="To" inputMode="numeric" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value.replace(/[^0-9]/g, ''))} data-testid="bulk-end" />
          </div>
          <input className={inputCls + ' w-full mb-3'} placeholder="Price each (optional)" inputMode="decimal" value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value.replace(/[^0-9.]/g, ''))} data-testid="bulk-price" />
          <button onClick={addBulk} disabled={!bulkStart || !bulkEnd || bulkBusy} style={{ cursor: 'pointer' }}
            data-testid="bulk-submit" className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#ccc] hover:border-[#90E0EF]/50 hover:text-white disabled:opacity-40">
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />} Add Range
          </button>
        </div>
      </div>

      {giftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }} onClick={() => !gBusy && setGiftModal(null)} data-testid="gift-modal">
          <div className="w-full max-w-md border border-[#222] bg-[#0a0a0a] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <p className="font-outfit text-lg text-white">
                {giftModal.mode === 'edit' ? 'Edit recipient' : 'Withhold card'} <span style={{ color: CY }}>#{giftModal.card.card_number_display}</span>
              </p>
              <button onClick={() => !gBusy && setGiftModal(null)} style={{ cursor: 'pointer' }} className="text-[#666] hover:text-white" data-testid="gift-modal-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] font-mono text-[#5a5a5a] mb-5 leading-relaxed">
              {giftModal.mode === 'edit'
                ? 'Update who holds this edition. With an email on file, they can be contacted if drawn.'
                : 'This marks the card as claimed (not a sale). Optionally record who you gave it to so they can take part in a draw.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[9px] font-mono uppercase tracking-[0.18em] text-[#666] mb-1.5">Recipient name <span className="text-[#444]">(optional)</span></label>
                <input className={inputCls + ' w-full'} placeholder="e.g. Jane Doe" value={gName} onChange={(e) => setGName(e.target.value)} data-testid="gift-name-input" />
              </div>
              <div>
                <label className="block text-[9px] font-mono uppercase tracking-[0.18em] text-[#666] mb-1.5">Recipient email <span className="text-[#444]">(optional)</span></label>
                <input className={inputCls + ' w-full'} placeholder="name@example.com" value={gEmail} onChange={(e) => setGEmail(e.target.value)} data-testid="gift-email-input" />
                <p className="text-[10px] font-mono text-[#555] mt-1.5">Leave blank if you never collected it — the card can still enter the no-email pool.</p>
              </div>
              <button onClick={submitGift} disabled={gBusy} style={{ cursor: gBusy ? 'not-allowed' : 'pointer', background: CY, color: '#050505' }}
                data-testid="gift-submit" className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] disabled:opacity-40">
                {gBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {giftModal.mode === 'edit' ? 'Save recipient' : 'Withhold card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
